import React from "react";
import { Bar, Doughnut, Line, Pie, Radar } from "react-chartjs-2";
import "chart.js/auto";
import { isDarkColor } from "../../../services/helpFunctions/colorLuminance";
import { HOME_INK, HOME_INK_SOFT, HOME_TRACK } from "./homeTheme";

/**
 * One chart look for the whole project dashboard — the Home reports and the TPM
 * / IP / Visit Data summary tabs.
 *
 * The brief was "simple, modern, not that colourful, and write the numbers on
 * the chart". So:
 *   * `valueLabels` prints every datum on its own mark. Nothing is hover-only,
 *     and the Word/PDF export of a chart carries its numbers.
 *   * `styleChart` normalises any options object a page already had: Poppins at
 *     11px, muted ink, no axis borders, gridlines only on the value axis, small
 *     circular legend swatches, compact 13px titles, animation off. The caller's
 *     own settings (stacked, min/max, tooltip callbacks, indexAxis…) survive —
 *     only presentation is overridden.
 *   * The `Tpm*` wrappers apply both, so a page swaps `<Bar>` for `<TpmBar>` and
 *     inherits the house style instead of restating it.
 *
 * Colours are NOT auto-transformed: pages pass palette entries from homeTheme,
 * which keeps the mapping from a value to a colour explicit and reviewable.
 */

const FONT = { family: "Poppins, sans-serif", size: 11 };
const TITLE_FONT = { family: "Poppins, sans-serif", size: 13, weight: "600" };

// ---- Value labels -------------------------------------------------------
export const valueLabels = {
  id: "tpmValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.font = "600 11px Poppins, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;
      // Radar/scatter labels would land on top of each other; those charts are
      // read as shapes, not as numbers.
      if (meta.type === "radar" || meta.type === "scatter") return;

      meta.data.forEach((element, index) => {
        const raw = dataset.data[index];
        const value = Number(raw);
        if (!Number.isFinite(value) || value === 0) return;
        const text = String(raw);

        if (meta.type === "doughnut" || meta.type === "pie") {
          const { x, y } = element.tooltipPosition();
          const fill = Array.isArray(dataset.backgroundColor)
            ? dataset.backgroundColor[index]
            : dataset.backgroundColor;
          ctx.fillStyle = isDarkColor(fill) ? "#ffffff" : HOME_INK;
          ctx.fillText(text, x, y);
          return;
        }

        const horizontal = chart.options.indexAxis === "y";
        const stacked =
          chart.options.scales?.x?.stacked || chart.options.scales?.y?.stacked;
        ctx.fillStyle = HOME_INK;

        if (stacked) {
          // A stacked segment is labelled inside itself — outside the bar would
          // be another segment's space.
          const { x, y } = element.tooltipPosition();
          const fill = Array.isArray(dataset.backgroundColor)
            ? dataset.backgroundColor[index]
            : dataset.backgroundColor;
          ctx.fillStyle = isDarkColor(fill) ? "#ffffff" : HOME_INK;
          ctx.fillText(text, x, y);
          return;
        }

        if (horizontal) {
          ctx.textAlign = "left";
          ctx.fillText(text, element.x + 6, element.y);
          ctx.textAlign = "center";
        } else {
          ctx.fillText(text, element.x, element.y - 8);
        }
      });
    });
    ctx.restore();
  },
};

// ---- Option normaliser --------------------------------------------------
const isObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

/** Caller's values win; defaults fill the gaps. */
const merge = (defaults, given) => {
  const out = { ...defaults };
  Object.keys(given || {}).forEach((key) => {
    out[key] =
      isObject(defaults[key]) && isObject(given[key])
        ? merge(defaults[key], given[key])
        : given[key];
  });
  return out;
};

const AXIS_DEFAULTS = {
  border: { display: false },
  ticks: { font: FONT, color: HOME_INK_SOFT, padding: 4 },
};

/**
 * `cartesian` says whether this chart type actually has x/y axes. Pies, donuts
 * and radars don't: handing them x/y scale configs is meaningless at best, and
 * for a radar it competes with its own `r` scale.
 */
export const styleChart = (options = {}, { cartesian = true } = {}) => {
  const horizontal = options.indexAxis === "y";

  const styled = merge(
    {
      responsive: true,
      // `maintainAspectRatio` is deliberately NOT defaulted: the summary tabs
      // let their charts derive height from width, and forcing it off would
      // collapse every canvas whose container has no height of its own.
      animation: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 8,
            boxHeight: 8,
            usePointStyle: true,
            pointStyle: "circle",
            padding: 12,
            color: HOME_INK_SOFT,
            font: FONT,
          },
        },
        tooltip: { enabled: true },
      },
      // Room for the labels drawn just past a bar's end.
      layout: cartesian
        ? { padding: horizontal ? { right: 26 } : { top: 16 } }
        : {},
      // Slim bars read as data rather than as blocks of colour, without every
      // page having to say so per dataset.
      ...(cartesian ? { datasets: { bar: { maxBarThickness: 34 } } } : {}),
      ...(cartesian ? { scales: { x: AXIS_DEFAULTS, y: AXIS_DEFAULTS } } : {}),
    },
    options
  );

  // Presentation is ours even when the page said otherwise: gridlines only on
  // the value axis, and a small dark title rather than an 18px brand heading.
  // A page that deliberately turns a grid off (the reports drop the value axis
  // entirely, so its gridlines would measure nothing) keeps that choice.
  ["x", "y"].forEach((axis) => {
    if (!styled.scales?.[axis]) return;
    const isValueAxis = horizontal ? axis === "x" : axis === "y";
    const asked = options.scales?.[axis]?.grid?.display;
    styled.scales[axis].grid = {
      display: asked === undefined ? isValueAxis : asked,
      color: HOME_TRACK,
      drawTicks: false,
    };
    styled.scales[axis].border = { display: false };
    styled.scales[axis].ticks = {
      ...styled.scales[axis].ticks,
      font: FONT,
      color: HOME_INK_SOFT,
    };

    // chart.js measures tick labels before the Poppins webfont has applied, so
    // on a horizontal bar chart a long category ("Data collection") ends up
    // clipped by a couple of characters. Buy the difference back, unless the
    // page sized the axis itself.
    if (horizontal && axis === "y" && !options.scales?.y?.afterFit) {
      styled.scales.y.afterFit = (scale) => {
        scale.width += 16;
      };
    }
  });

  if (styled.plugins?.title?.display) {
    styled.plugins.title = {
      ...styled.plugins.title,
      font: TITLE_FONT,
      color: HOME_INK,
      padding: { bottom: 10 },
    };
  }

  return styled;
};

// ---- Chart components ---------------------------------------------------
const withStyle = (
  Component,
  { cartesian = true, extraPlugins = [valueLabels], name } = {}
) => {
  const Styled = ({ options, plugins = [], ...rest }) => (
    <Component
      {...rest}
      options={styleChart(options, { cartesian })}
      plugins={[...extraPlugins, ...plugins]}
    />
  );
  Styled.displayName = `Tpm${name || "Chart"}`;
  return Styled;
};

export const TpmBar = withStyle(Bar, { name: "Bar" });
export const TpmLine = withStyle(Line, { name: "Line" });
export const TpmPie = withStyle(Pie, { cartesian: false, name: "Pie" });
export const TpmDonut = withStyle(Doughnut, {
  cartesian: false,
  name: "Donut",
});
// Radar reads as a shape; numbers on every vertex would be noise, and its scale
// is radial rather than x/y.
export const TpmRadar = withStyle(Radar, {
  cartesian: false,
  extraPlugins: [],
  name: "Radar",
});

export default TpmBar;
